import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';

interface LOIDocument {
  id: string;
  deal_id: string;
  buyer_name: string;
  seller_name: string;
  buyer_email?: string;
  seller_email?: string;
  loi_type: string;
  status: string;
  purchase_price?: number;
  currency: string;
  due_diligence_days: number;
  exclusivity_days: number;
  closing_date?: string;
  terms_and_conditions?: string;
  special_conditions?: string;
  created_at: string;
  updated_at: string;
  has_buyer_signature: boolean;
  all_signatures_complete: boolean;
}

export function useLOIDocuments(dealId: string | null) {
  const { user } = useUser();
  const [loiDocuments, setLoiDocuments] = useState<LOIDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLOIDocuments = async () => {
    if (!dealId || !user?.id) {
      setLoiDocuments([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await DatabaseService.getDealLOIDocuments(dealId, user.id);
      if (response.data) {
        setLoiDocuments(response.data);
      } else if (response.error) {
        setError(response.error);
        setLoiDocuments([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load LOI documents');
      setLoiDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLOIDocuments();
  }, [dealId, user?.id]);

  const hasLOIDocuments = loiDocuments.length > 0;
  const hasOption1 = loiDocuments.some(doc => doc.loi_type === 'option1');
  const hasOption2 = loiDocuments.some(doc => doc.loi_type === 'option2');

  return {
    loiDocuments,
    isLoading,
    error,
    hasLOIDocuments,
    hasOption1,
    hasOption2,
    refreshLOIDocuments: loadLOIDocuments
  };
}
